import { VBtn } from "vuetify/components";

/**
 * The four admin button roles (apps/contact-admin/CLAUDE.md "Button Conventions") as Vuetify aliases.
 * `<PrimaryBtn>` is a real VBtn with the role's props as defaults, so it keeps VBtn's whole API (size,
 * block, icons, loading, to, ...) and one change here restyles every button in that role.
 *
 * Two Vuetify rules make the defaults below longer than the role table:
 * - An alias reads only its own defaults key plus `global`, never `VBtn`'s, so the app-wide
 *   `VBtn: { elevation: 0 }` is repeated for each role.
 * - Containers such as VCardActions push defaults to the `VBtn` key, which an alias also misses. Every
 *   role sets color, variant and rounded itself, so the one injected prop that would be lost is `slim`;
 *   it is restored for the same containers below. buttonRoleAliases.spec.ts proves each role renders
 *   exactly like the raw v-btn it replaces, inside and outside those containers.
 */
export const buttonAliases = {
	PrimaryBtn: VBtn,
	SecondaryBtn: VBtn,
	TertiaryBtn: VBtn,
	DangerBtn: VBtn
};

type ButtonRole = keyof typeof buttonAliases;

const roleDefaults: Record<ButtonRole, Record<string, unknown>> = {
	PrimaryBtn: { color: "primary", variant: "flat", rounded: "xl", elevation: 0 },
	SecondaryBtn: { color: "primary", variant: "outlined", rounded: "xl", elevation: 0 },
	TertiaryBtn: { color: "grey", variant: "text", rounded: "xl", elevation: 0 },
	DangerBtn: { color: "error", variant: "flat", rounded: "xl", elevation: 0 }
};

const slimInside = Object.fromEntries(Object.keys(buttonAliases).map((role) => [role, { slim: true }]));

export const buttonDefaults = {
	...roleDefaults,
	VCardActions: slimInside,
	VBannerActions: slimInside
};
